import { useEffect, useState } from "react";
import { adminService } from "../../services/api";

const SiteSettings = () => {
    const [settings, setSettings] = useState({
        notAcceptingOrders: false,
        websiteUnderMaintenance: false,
        comingSoon: false,
        customMarqueeMessage: ""
    });
    const [customMessageInput, setCustomMessageInput] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await adminService.getSiteSettings();
            if (response.status === "success" && response.data.settings) {
                const fetchedSettings = response.data.settings;
                const customMsg = fetchedSettings.customMarqueeMessage || "";
                setSettings({
                    notAcceptingOrders: fetchedSettings.notAcceptingOrders || false,
                    websiteUnderMaintenance: fetchedSettings.websiteUnderMaintenance || false,
                    comingSoon: fetchedSettings.comingSoon || false,
                    customMarqueeMessage: customMsg
                });
                setCustomMessageInput(customMsg);
            }
        } catch (error) {
            console.error("Failed to fetch site settings:", error);
            setMessage({ type: "error", text: "Failed to load settings" });
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async (field) => {
        const newValue = !settings[field];
        const updatedSettings = { ...settings, [field]: newValue };

        setSaving(true);
        setMessage({ type: "", text: "" });

        try {
            const response = await adminService.updateSiteSettings(updatedSettings);
            if (response.status === "success") {
                setSettings(response.data.settings);
                setMessage({
                    type: "success",
                    text: "Settings updated successfully"
                });
                setTimeout(() => setMessage({ type: "", text: "" }), 3000);
            }
        } catch (error) {
            console.error("Failed to update settings:", error);
            setMessage({
                type: "error",
                text: "Failed to update settings. Please try again."
            });
            setTimeout(() => setMessage({ type: "", text: "" }), 3000);
        } finally {
            setSaving(false);
        }
    };

    const handleCustomMessageInput = (value) => {
        setCustomMessageInput(value);
    };

    const handleCustomMessageSave = async () => {
        setSaving(true);
        setMessage({ type: "", text: "" });

        const updatedSettings = {
            ...settings,
            customMarqueeMessage: customMessageInput
        };

        try {
            const response = await adminService.updateSiteSettings(updatedSettings);
            if (response.status === "success" && response.data.settings) {
                const savedSettings = response.data.settings;
                setSettings({
                    notAcceptingOrders: savedSettings.notAcceptingOrders || false,
                    websiteUnderMaintenance: savedSettings.websiteUnderMaintenance || false,
                    comingSoon: savedSettings.comingSoon || false,
                    customMarqueeMessage: savedSettings.customMarqueeMessage || ""
                });
                setCustomMessageInput(savedSettings.customMarqueeMessage || "");
                setMessage({
                    type: "success",
                    text: "Custom message updated successfully"
                });
                setTimeout(() => setMessage({ type: "", text: "" }), 3000);
            }
        } catch (error) {
            console.error("Failed to update settings:", error);
            setMessage({
                type: "error",
                text: "Failed to update settings. Please try again."
            });
            setTimeout(() => setMessage({ type: "", text: "" }), 3000);
        } finally {
            setSaving(false);
        }
    };

    const handleCustomMessageClear = () => {
        setCustomMessageInput("");
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-200 border-t-[#008000]"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-6 lg:p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Site Status Settings</h2>
                <p className="text-gray-600 mb-6">
                    Control site-wide functionality and display status messages to users
                </p>

                {message.text && (
                    <div className={`mb-6 p-4 rounded-lg ${message.type === "success"
                        ? "bg-green-50 text-[#008000] border border-green-200"
                        : "bg-red-50 text-red-800 border border-red-200"
                        }`}>
                        {message.text}
                    </div>
                )}

                <div className="space-y-6">
                    <div className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                    Not Currently Accepting Orders
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Disables checkout functionality and shows a message to users that orders are temporarily unavailable. A marquee will be displayed above the hero section.
                                </p>
                            </div>
                            <button
                                onClick={() => handleToggle("notAcceptingOrders")}
                                disabled={saving}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${settings.notAcceptingOrders ? "bg-red-600" : "bg-gray-200"
                                    } ${saving ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                                <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${settings.notAcceptingOrders ? "translate-x-5" : "translate-x-0"
                                        }`}
                                />
                            </button>
                        </div>
                        {settings.notAcceptingOrders && (
                            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm text-red-800">
                                    <strong>Active:</strong> Orders are currently disabled. Users will see a message and cannot proceed to checkout.
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                    Website Under Maintenance
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Disables product browsing and category pages. Users can still view their orders. A marquee will be displayed above the hero section.
                                </p>
                            </div>
                            <button
                                onClick={() => handleToggle("websiteUnderMaintenance")}
                                disabled={saving}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${settings.websiteUnderMaintenance ? "bg-yellow-600" : "bg-gray-200"
                                    } ${saving ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                                <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${settings.websiteUnderMaintenance ? "translate-x-5" : "translate-x-0"
                                        }`}
                                />
                            </button>
                        </div>
                        {settings.websiteUnderMaintenance && (
                            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <p className="text-sm text-yellow-800">
                                    <strong>Active:</strong> Product browsing is disabled. Users can still access their orders.
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                    Coming Soon
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Shows a "Coming Soon" page on the landing page and disables all routes except admin panel. This is the most restrictive mode.
                                </p>
                            </div>
                            <button
                                onClick={() => handleToggle("comingSoon")}
                                disabled={saving}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${settings.comingSoon ? "bg-purple-600" : "bg-gray-200"
                                    } ${saving ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                                <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${settings.comingSoon ? "translate-x-5" : "translate-x-0"
                                        }`}
                                />
                            </button>
                        </div>
                        {settings.comingSoon && (
                            <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                <p className="text-sm text-purple-800">
                                    <strong>Active:</strong> All routes are disabled except admin panel. Landing page shows "Coming Soon" message.
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                Custom Marquee Message
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                                Set a custom message to display in the marquee. If left empty, default messages will be shown based on the active status toggles above.
                            </p>
                            <div className="mt-4">
                                <textarea
                                    value={customMessageInput}
                                    onChange={(e) => handleCustomMessageInput(e.target.value)}
                                    placeholder="Enter custom marquee message (e.g., 'Special announcement: Free shipping on all orders!')"
                                    maxLength={200}
                                    rows={3}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
                                    disabled={saving}
                                />
                                <div className="mt-4 flex justify-between items-center">
                                    <p className="text-sm text-gray-500">
                                        {customMessageInput?.length || 0} / 200 characters
                                    </p>
                                    <div className="flex gap-3">
                                        {customMessageInput && (
                                            <button
                                                onClick={handleCustomMessageClear}
                                                disabled={saving}
                                                className="px-6 py-2.5 text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 hover:border-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Clear
                                            </button>
                                        )}
                                        <button
                                            onClick={handleCustomMessageSave}
                                            disabled={saving || customMessageInput === (settings.customMarqueeMessage || "")}
                                            className="px-6 py-2.5 text-sm font-semibold text-white bg-[#008000] rounded-xl hover:bg-[#008000] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                                        >
                                            {saving ? "Saving..." : "Save"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SiteSettings;

